// src/components/Footer.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LinkIcon from '@mui/icons-material/Link';
import './Footer.css';
import { getFooterItems, FooterItem } from '../api/footerService';

// Group items by category within a section
function groupByCategory(items: FooterItem[]): Record<string, FooterItem[]> {
  return items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, FooterItem[]>);
}

const Footer: React.FC = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const [items, setItems] = useState<FooterItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFooterItems()
      .then(setItems)
      .catch(() => setItems([]))   // fail gracefully — footer still renders
      .finally(() => setLoading(false));
  }, []);

  const skillItems  = items.filter(i => i.section === 'skills');
  const socialItems = items.filter(i => i.section === 'social');
  const linkItems   = items.filter(i => i.section === 'links');

  const skillGroups = groupByCategory(skillItems);

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: theme.palette.primary.main,
        color: 'white',
        py: { xs: 4, md: 6 },
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress color="inherit" size={28} />
          </Box>
        ) : (
          <>
            {/* ── Skill columns ─────────────────────────────────── */}
            <Grid container spacing={3} justifyContent="space-between">
              {Object.entries(skillGroups).map(([category, catItems]) => (
                <Grid
                  {...{ item: true, xs: 12, sm: 6, md: 3 }}
                  key={category}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    className="footer-category-title"
                  >
                    {category}
                  </Typography>
                  <Box className="footer-skill-list">
                    {catItems.map((item, idx) => (
                      <React.Fragment key={item.id}>
                        <Typography variant="body2" className="footer-skill-item">
                          {item.label}
                        </Typography>
                        {idx < catItems.length - 1 && (
                          <Divider
                            sx={{ my: 0.5, bgcolor: 'rgba(255,255,255,0.2)' }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* ── Social + Links row ────────────────────────────── */}
            {(socialItems.length > 0 || linkItems.length > 0) && (
              <Box
                sx={{
                  mt: 5,
                  pt: 3,
                  borderTop: '1px solid rgba(255,255,255,0.2)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                {socialItems.map(item => (
                  <IconButton
                    key={item.id}
                    component="a"
                    href={item.url ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.label}
                    sx={{
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.5)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                    }}
                  >
                    {item.label.toLowerCase().includes('linkedin')
                      ? <LinkedInIcon />
                      : <LinkIcon />}
                  </IconButton>
                ))}

                {linkItems.map(item => (
                  <MuiLink
                    key={item.id}
                    href={item.url ?? '#'}
                    color="inherit"
                    underline="hover"
                    sx={{
                      fontSize: '0.875rem',
                      opacity: 0.75,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    {item.label}
                  </MuiLink>
                ))}
              </Box>
            )}
          </>
        )}

        {/* ── Contact Me button ─────────────────────────────────── */}
        <Box
          sx={{
            mt: 5,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<EmailIcon />}
            component={RouterLink}
            to="/contact"
            sx={{
              borderColor: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
                borderColor: 'white',
              },
            }}
          >
            Contact Me
          </Button>
        </Box>

        {/* ── Copyright ─────────────────────────────────────────── */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="rgba(255,255,255,0.7)">
            © {currentYear} Ilisio Viana. All rights reserved.
          </Typography>
        </Box>

      </Container>
    </Box>
  );
};

export default Footer;
